import { call, put, select, takeLatest } from 'redux-saga/effects';

import type {
  GetTeamMemberResponseBody,
  GetTeamMembersResponseBody,
  RequestQuery,
  ResendInviteResponseBody,
  SendInviteResponseBody,
  UpdateTeamMemberResponseBody,
} from '@frontend/api';

import authService from 'services/authService';
import teamService from 'services/teamService';

import * as SESSION from 'store/session';
import type { ActionOfType } from 'store/types';

import history from 'utils/browserHistory';

import * as actions from './actions';
import { resendInvite, type SupplierAction } from './actions';
import * as selectors from './selectors';
import * as types from './types';

export function* getTeamMembers(apiParams?: RequestQuery) {
  try {
    const groupId: ReturnType<typeof SESSION.selectors.getGroupId> =
      yield select(SESSION.selectors.getGroupId);
    const response: GetTeamMembersResponseBody = yield call(
      teamService.getTeamMembers,
      groupId,
      apiParams,
    );
    yield put(actions.getTeamMembers.success({ teamMembers: response }));
  } catch (error) {
    yield put(actions.getTeamMembers.error({ error }));
  }
}

function* watchGetTeamMembers() {
  yield call(getTeamMembers);
}

export function* getTeamMember(teamMemberId: number | string) {
  try {
    const response: GetTeamMemberResponseBody = yield call(
      teamService.getTeamMember,
      teamMemberId,
    );
    yield put(actions.getTeamMember.success({ teamMember: response }));
    yield put(
      actions.checkEmailVerification.request({ teamMemberId: response.userId }),
    );
  } catch (error) {
    yield put(actions.getTeamMember.error({ error }));
  }
}

function* watchDeleteTeamMember(
  action: ActionOfType<SupplierAction, typeof types.DELETE_TEAM_MEMBER_REQUEST>,
) {
  try {
    const { teamMemberId } = action.payload;
    yield call(teamService.deleteTeamMember, teamMemberId);
    yield put(actions.deleteTeamMember.success({ teamMemberId }));
  } catch (error) {
    yield put(actions.deleteTeamMember.error({ error }));
  }
}

function* watchCreateTeamMember(
  action: ActionOfType<SupplierAction, typeof types.CREATE_TEAM_MEMBER_REQUEST>,
) {
  try {
    const { teamMember } = action.payload;
    const groupId: ReturnType<typeof SESSION.selectors.getGroupId> =
      yield select(SESSION.selectors.getGroupId);
    const response: SendInviteResponseBody = yield call(
      teamService.inviteTeamMember,
      groupId,
      teamMember,
    );
    history.push('/team');
    yield put(actions.createTeamMember.success({ response }));
  } catch (error) {
    yield put(actions.createTeamMember.error({ error }));
  }
}

function* watchResendTeamMemberInvite(
  action: ActionOfType<
    SupplierAction,
    typeof types.TEAM_MEMBER_INVITE_RESEND_REQUEST
  >,
) {
  try {
    const groupId: ReturnType<typeof SESSION.selectors.getGroupId> =
      yield select(SESSION.selectors.getGroupId);
    const { emailAddress } = action.payload;
    const response: ResendInviteResponseBody = yield call(
      resendTeamMemberInvite,
      {
        groupId,
        emailAddress,
      },
    );
    yield put(resendInvite.success({ response }));
    yield call(getTeamMembers);
  } catch (error) {
    yield put(resendInvite.error(error));
  }
}

function* resendTeamMemberInvite({
  groupId,
  emailAddress,
}: {
  groupId: string;
  emailAddress: string;
}): Generator<
  Promise<ResendInviteResponseBody>,
  ResendInviteResponseBody,
  ResendInviteResponseBody
> {
  return yield teamService.invites.resend(groupId, {
    emailAddress,
  });
}

function* watchUpdateTeamMember(
  action: ActionOfType<SupplierAction, typeof types.UPDATE_TEAM_MEMBER_REQUEST>,
) {
  try {
    const { teamMember, cb } = action.payload;
    const response: UpdateTeamMemberResponseBody = yield call(
      teamService.updateTeamMember,
      teamMember.id,
      teamMember,
    );
    const user: ReturnType<typeof SESSION.selectors.getUser> = yield select(
      SESSION.selectors.getUser,
    );
    if (teamMember.email === user.email) {
      const user: Awaited<ReturnType<typeof authService.profile>> = yield call(
        authService.profile,
      );
      yield put(SESSION.actions.receiveUser(user));
    }
    yield put(actions.updateTeamMember.success({ teamMember: response }));
    cb && cb();
  } catch (error) {
    yield put(actions.updateTeamMember.error({ error }));
  }
}

function* watchSendVerificationEmail() {
  try {
    const { userId }: ReturnType<typeof selectors.getActiveTeamMember> =
      yield select(selectors.getActiveTeamMember);
    yield call(teamService.sendVerificationEmail, userId);
    yield put(actions.sendVerificationEmail.success());
  } catch (error) {
    yield put(actions.sendVerificationEmail.error({ error }));
  }
}

export function* watchCheckEmailVerification() {
  try {
    const { userId }: ReturnType<typeof selectors.getActiveTeamMember> =
      yield select(selectors.getActiveTeamMember);
    const isVerified: boolean = yield call(
      teamService.getIsEmailVerified,
      userId,
    );
    yield put(actions.checkEmailVerification.success({ isVerified }));
  } catch (error) {
    yield put(actions.checkEmailVerification.error({ error }));
  }
}

export default function* root() {
  yield takeLatest(types.GET_TEAM_MEMBERS_REQUEST, watchGetTeamMembers);
  yield takeLatest(types.DELETE_TEAM_MEMBER_REQUEST, watchDeleteTeamMember);
  yield takeLatest(types.CREATE_TEAM_MEMBER_REQUEST, watchCreateTeamMember);
  yield takeLatest(types.UPDATE_TEAM_MEMBER_REQUEST, watchUpdateTeamMember);
  yield takeLatest(
    types.TEAM_MEMBER_INVITE_RESEND_REQUEST,
    watchResendTeamMemberInvite,
  );
  yield takeLatest(
    types.SEND_VERIFICATION_EMAIL_REQUEST,
    watchSendVerificationEmail,
  );
  yield takeLatest(
    types.CHECK_EMAIL_VERIFICATION_REQUEST,
    watchCheckEmailVerification,
  );
}
